import _ from 'lodash';

import aggregateMetricMatrix from 'api/aggregateMetricMatrix';
import isDefined from 'isDefined';
import metricTypeCheckers from 'types/metricTypeCheckers';
import buildGroupBy from 'hooks/useV5ChartData/buildGroupBy';
import buildSubGroupBy from 'hooks/useV5ChartData/buildSubGroupBy';
import buildGroupByInterval from 'hooks/useV5ChartData/buildGroupByInterval';
import buildGroupByRanges from 'hooks/useV5ChartData/buildGroupByRanges';

import getGroupByField from '../getGroupByField';

const getMetrics = async ({
  chartDef,
  filterInput,
  getTotals,
  usedMetrics,
  autoInterval,
  dateScope,
  toMetricInput,
  client,
  limitedKeywordFilter,
  isIgnoringLimit,
  forecastPeriods,
}: Queries.GetMetricsInput): Promise<MetricsResponse> => {
  const groupByField = getGroupByField(chartDef, autoInterval);

  const normalMetrics = usedMetrics.filter(metricTypeCheckers.isNormalMetric);
  const specialMetrics = _.uniqBy(
    usedMetrics.filter(metricTypeCheckers.isSpecialMetric),
    'id',
  );
  const compoundMetrics = usedMetrics.filter(
    metricTypeCheckers.isCompoundMetric,
  );
  const metricsFromCompoundMetrics = (() => {
    let metrics = [] as Metrics.NormalMetric[];
    compoundMetrics.forEach((compoundM) => {
      const metricsForM = compoundM.metricIds
        .map((mId) => normalMetrics.find((m) => m.id === mId))
        .filter(isDefined);
      metrics = [...metrics, ...metricsForM];
    });
    return _.uniqBy(metrics, 'id');
  })();
  const metricInputs = _.uniqBy(
    [...normalMetrics, ...metricsFromCompoundMetrics].map((m) =>
      toMetricInput(m),
    ),
    'id',
  );
  const queryMetrics = [...specialMetrics, ...metricInputs];

  const filterInputWithLimits = ((): FilterInput | undefined => {
    if (limitedKeywordFilter) {
      return {
        ...filterInput,
        keywords: [
          ...(filterInput.keywords || []).filter(
            // Remove existing keywords for the grouped term
            (k) => k.field !== groupByField,
          ),
          limitedKeywordFilter,
        ],
      };
    }
    return undefined;
  })();

  const queryExpressions = usedMetrics
    .filter(metricTypeCheckers.isCompoundMetric)
    .map((m) => {
      return {
        id: m.id,
        expression: m.expression,
      } as Expression2;
    });

  const shouldApplyAutoTrend = (() => {
    const autoTrendTypes = [
      'timeSeries',
      'bar',
      'horizontalBar',
      'combo',
      'matrix',
    ];
    return (
      !chartDef.trendByCalendarInterval &&
      autoTrendTypes.includes(chartDef.gadgetType) &&
      chartDef.groupByDayOfWeek === false &&
      chartDef.groupByMonth === false
    );
  })();

  const trendByCalendarInterval = (() => {
    if (!getTotals) {
      return chartDef.trendByCalendarInterval;
    }

    if (chartDef.dimensionA && chartDef.dimensionA.isGroupByMonth) {
      return chartDef.trendByCalendarInterval;
    }

    if (!filterInputWithLimits) {
      return undefined;
    }

    if (shouldApplyAutoTrend) {
      return 'auto';
    }

    return chartDef.trendByCalendarInterval;
  })();

  return aggregateMetricMatrix({
    client,
    chartDefId: chartDef.id,
    metrics: queryMetrics,
    expressions: queryExpressions,
    filterInput: [filterInputWithLimits ? filterInputWithLimits : filterInput],
    groupBy:
      groupByField && !getTotals
        ? buildGroupBy(chartDef, isIgnoringLimit)
        : undefined,
    // Nest a second categorical dimension (terms x terms). Only the row-level
    // request carries it — the totals request stays 1D per outer term.
    subGroupBy:
      groupByField && !getTotals ? buildSubGroupBy(chartDef) : undefined,
    trendByCalendarInterval,
    trendByFixedIntervalDays:
      getTotals && !filterInputWithLimits
        ? undefined
        : chartDef.trendByFixedIntervalDays,
    trendByFiscalInterval:
      getTotals && !filterInputWithLimits
        ? undefined
        : chartDef.trendByFiscalInterval,
    groupByDayOfWeek:
      getTotals && !filterInputWithLimits
        ? undefined
        : chartDef.groupByDayOfWeek,
    groupByRange:
      getTotals && !filterInputWithLimits
        ? undefined
        : buildGroupByRanges(chartDef),
    groupByInterval:
      getTotals && !filterInputWithLimits
        ? undefined
        : buildGroupByInterval(chartDef),
    groupByHour:
      getTotals && !filterInputWithLimits
        ? undefined
        : chartDef.trendByHourInterval,
    groupByMonth:
      getTotals && !filterInputWithLimits ? undefined : chartDef.groupByMonth,
    forecastPeriods: getTotals ? undefined : forecastPeriods,
    dateScope,
  });
};

export default getMetrics;
